import create, { GetState, SetState } from 'zustand';
import { devtools } from 'zustand/middleware';

import { SupportedSubjects } from 'types/Subject';

import {
  clearSheetsTimerSession,
  loadSheetsTimerSession,
  resolveElapsedFromSession,
  resolveSheetsTimerDuration,
  saveSheetsTimerDuration,
  saveSheetsTimerSession,
} from 'uniqueComponents/Sheets/SheetsTimer/sheetsTimerPersist';

const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

const persistActiveSession = (state: SheetsTimerStore): void => {
  const { subjectCode, activeSheetKey, elapsedSeconds, isManuallyPaused, durationMinutes } = state;

  if (!subjectCode || !activeSheetKey || durationMinutes == null) {
    return;
  }

  saveSheetsTimerSession(subjectCode, activeSheetKey, {
    elapsedSeconds,
    savedAt: Date.now(),
    isManuallyPaused,
  });
};

type SheetsTimerStore = {
  durationMinutes: number | null;
  elapsedSeconds: number;
  activeSheetKey: string | null;
  isRunning: boolean;
  isManuallyPaused: boolean;
  subjectCode: SupportedSubjects | null;

  initForSubject: (subjectCode: SupportedSubjects) => void;
  setDurationMinutes: (durationMinutes: number | null) => void;
  startForSheet: (sheetKey: string, options?: { resetElapsed?: boolean }) => void;
  stop: (options?: { clearPersisted?: boolean }) => void;
  pause: () => void;
  resume: () => void;
  pauseForRouteLeave: () => void;
  resumeAfterRouteReturn: () => void;
  reset: () => void;
  tick: () => void;
};

const sheetsTimerStore = (set: SetState<SheetsTimerStore>, get: GetState<SheetsTimerStore>) => ({
  durationMinutes: null,
  elapsedSeconds: 0,
  activeSheetKey: null,
  isRunning: false,
  isManuallyPaused: false,
  subjectCode: null,

  initForSubject: (subjectCode: SupportedSubjects) => {
    const { subjectCode: currentSubjectCode } = get();

    if (currentSubjectCode === subjectCode) {
      return;
    }

    set({
      subjectCode,
      durationMinutes: resolveSheetsTimerDuration(subjectCode),
      elapsedSeconds: 0,
      activeSheetKey: null,
      isRunning: false,
      isManuallyPaused: false,
    });
  },

  setDurationMinutes: (durationMinutes: number | null) => {
    const { subjectCode, activeSheetKey } = get();
    if (subjectCode) {
      saveSheetsTimerDuration(subjectCode, durationMinutes);
    }

    if (activeSheetKey == null) {
      set({ durationMinutes });
      return;
    }

    if (subjectCode && activeSheetKey) {
      clearSheetsTimerSession(subjectCode, activeSheetKey);
    }

    const nextState = {
      durationMinutes,
      elapsedSeconds: 0,
      isManuallyPaused: false,
      isRunning: durationMinutes != null,
    };

    set(nextState);
    persistActiveSession({ ...get(), ...nextState });
  },

  startForSheet: (sheetKey: string, { resetElapsed = false } = {}) => {
    const { activeSheetKey, subjectCode } = get();
    if (activeSheetKey === sheetKey && !resetElapsed) {
      return;
    }

    let elapsedSeconds = 0;
    let isManuallyPaused = false;
    let isRunning = true;

    if (!resetElapsed && subjectCode) {
      const session = loadSheetsTimerSession(subjectCode, sheetKey);
      if (session) {
        elapsedSeconds = resolveElapsedFromSession(session);
        isManuallyPaused = session.isManuallyPaused;
        isRunning = !isManuallyPaused;
      }
    }

    const nextState = {
      activeSheetKey: sheetKey,
      elapsedSeconds,
      isRunning,
      isManuallyPaused,
    };

    set(nextState);
    persistActiveSession({ ...get(), ...nextState });
  },

  stop: ({ clearPersisted = false } = {}) => {
    const { subjectCode, activeSheetKey } = get();

    if (clearPersisted && subjectCode && activeSheetKey) {
      clearSheetsTimerSession(subjectCode, activeSheetKey);
    }

    set({
      activeSheetKey: null,
      elapsedSeconds: 0,
      isRunning: false,
      isManuallyPaused: false,
    });
  },

  pause: () => {
    const { activeSheetKey, durationMinutes } = get();
    if (!activeSheetKey || durationMinutes == null) {
      return;
    }

    const nextState = { isRunning: false, isManuallyPaused: true };
    set(nextState);
    persistActiveSession({ ...get(), ...nextState });
  },

  resume: () => {
    const { activeSheetKey, durationMinutes } = get();
    if (!activeSheetKey || durationMinutes == null) {
      return;
    }

    const nextState = { isRunning: true, isManuallyPaused: false };
    set(nextState);
    persistActiveSession({ ...get(), ...nextState });
  },

  pauseForRouteLeave: () => {
    const { isRunning, activeSheetKey } = get();
    if (!isRunning || !activeSheetKey) {
      return;
    }

    set({ isRunning: false });
  },

  resumeAfterRouteReturn: () => {
    const { isManuallyPaused, activeSheetKey, durationMinutes } = get();
    if (isManuallyPaused || !activeSheetKey || durationMinutes == null) {
      return;
    }

    set({ isRunning: true });
  },

  reset: () => {
    set({
      durationMinutes: null,
      elapsedSeconds: 0,
      activeSheetKey: null,
      isRunning: false,
      isManuallyPaused: false,
      subjectCode: null,
    });
  },

  tick: () => {
    const { isRunning } = get();
    if (!isRunning) {
      return;
    }

    set(state => {
      const nextState = { elapsedSeconds: state.elapsedSeconds + 1 };
      persistActiveSession({ ...state, ...nextState });
      return nextState;
    });
  },
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useSheetsTimerStore = create(devtools(sheetsTimerStore, { name: 'SHEETS_TIMER', enabled: !isProduction }));

export default useSheetsTimerStore;
